import { Subject } from 'rxjs';
import { GameServices } from '../../services/game-services';
import { CardViewModel } from '../cards/card-view-model';
import { TwentyOne } from 'game-logic';
import { RoundedLabelViewModel } from '../components/rounded-label-view-model';
import { UxMetrics } from '../../model/ux-metrics';
import { delay } from 'src/games/common/helper';
import { TwentyOneStylesType } from '../../model/twentyone-types';

export class CardDeckViewModel {
  public get logicEventQueue$() {
    return this.gameServices.twentyOneGameRunner.logicEventQueue$;
  }
  public get layoutConfig() {
    return this.gameServices.gameMetricsService.gameLayoutConfig;
  }
  public get cardViewModelChanges$() {
    return this.cardViewModelChanges.asObservable();
  }
  public get onCountLabelChanged$() {
    return this.onCountLabelChanged;
  }

  private cardViewModels: Map<number, CardViewModel> = new Map();
  private readonly cardViewModelChanges = new Subject<{
    type: 'add' | 'remove';
    viewModel: CardViewModel;
    delay: number;
    instant: boolean;
  }>();
  private readonly onCountLabelChanged = new Subject<boolean>();
  public readonly countLabelViewModel: RoundedLabelViewModel;

  constructor(private gameServices: GameServices) {
    this.countLabelViewModel = new RoundedLabelViewModel(
      gameServices,
      TwentyOneStylesType.DeckLabelFill,
      TwentyOneStylesType.DeckBoxColor,
    );
  }

  public async addDeckCard(card: TwentyOne.Card, index: number, instant: boolean): Promise<void> {
    const cardViewModel = new CardViewModel(this.gameServices, card, index);
    this.cardViewModels.set(index, cardViewModel);
    this.cardViewModelChanges.next({ type: 'add', viewModel: cardViewModel, delay: 0, instant });

    if (!instant) {
      await delay(UxMetrics.CardDealDuration);
    }
  }

  public removeCard(cardIndex: number | undefined, instant: boolean): void {
    if (cardIndex === undefined) {
      return;
    }

    const removedItem = this.cardViewModels.get(cardIndex);
    if (removedItem) {
      this.cardViewModelChanges.next({
        type: 'remove',
        viewModel: removedItem,
        delay: 0,
        instant,
      });
      this.cardViewModels.delete(cardIndex);
    }
  }

  public resetGameScene() {
    this.clearAllCards();
    this.onCountLabelChanged.next(false);
    this.countLabelViewModel.setText('');
  }

  public updateDeckCount(text: string) {
    this.onCountLabelChanged.next(text !== '');
    this.countLabelViewModel.setText(text);
  }

  private async clearAllCards(): Promise<void> {
    if (this.cardViewModels.size === 0) {
      return;
    }

    const delayMs = UxMetrics.CardClearDelay / this.cardViewModels.size;
    for (const cardViewModel of Array.from(this.cardViewModels.values()).slice().reverse()) {
      this.cardViewModelChanges.next({
        type: 'remove',
        viewModel: cardViewModel,
        delay: UxMetrics.CardClearDelay,
        instant: false,
      });
      await delay(delayMs);
    }
    this.cardViewModels.clear();
  }
}
